import { reportsService } from '@/services';
import type {
  GetReceiptsApiParams,
  ReceiptApiRow,
  ReceiptsResponse,
} from '@/services';
import type { GetReceiptsParams, Receipt } from '@/services';
import { useQuery } from '@tanstack/react-query';

/** Coluna do grid (App) → chave de ordenação do backend. */
const SORT_KEY_BY_COLUMN: Record<string, string> = {
  receiptNumber: 'recibo',
  netValue: 'valor',
};

/** Tipo de favorecido (App) → código do backend (1 = Assessoria, 2 = Corretor). */
const BENEFICIARY_CODE: Record<string, number> = { advisory: 1, broker: 2 };

/** Traduz os filtros da tela para os query params do Portal.Api. */
export function toReceiptsApiParams(
  params: GetReceiptsParams
): GetReceiptsApiParams {
  return {
    page: params.page,
    pageSize: params.pageSize,
    sortField: params.sortField
      ? SORT_KEY_BY_COLUMN[params.sortField]
      : undefined,
    sortType: params.sortType,
    months: params.months,
    dateFrom: params.dateFrom || undefined,
    dateTo: params.dateTo || undefined,
    beneficiaryType: params.beneficiaryType
      ? BENEFICIARY_CODE[params.beneficiaryType]
      : undefined,
    advisoryPartyCode: params.advisory || undefined,
    brokerPartyCode: params.broker || undefined,
    // O SQL filtra por cd_status = 132 (Pago); "Pendente" não é um código único.
    statusCode: params.status === 'paid' ? 132 : undefined,
    search: params.search || undefined,
  };
}

/** Mapeia a linha crua do Portal.Api para o `Receipt` da tela. */
export function mapReceiptApiRow(row: ReceiptApiRow, index: number): Receipt {
  return {
    id: String(row.receiptId ?? row.receiptNumber ?? index),
    receiptId: row.receiptId ?? undefined,
    receiptNumber: row.receiptNumber ?? 0,
    netValue: row.netAmount ?? 0,
    paymentForecast: row.paymentForecast ?? undefined,
    paymentDate: row.paymentDate ?? undefined,
    status: row.status === 'Pago' ? 'paid' : 'pending',
    favorecidoName: row.beneficiaryName ?? undefined,
    favorecidoDocument: row.beneficiaryTaxId ?? undefined,
    beneficiaryType:
      row.beneficiaryType === 2
        ? 'broker'
        : row.beneficiaryType === 1
          ? 'advisory'
          : undefined,
  };
}

export function useReceipts(params: GetReceiptsParams = {}) {
  return useQuery({
    queryKey: ['financial-receipts', params],
    queryFn: async (): Promise<ReceiptsResponse> => {
      const response = await reportsService.listReceipts(
        toReceiptsApiParams(params)
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao carregar recibos');
      }
      const { data, meta } = response.data;
      return {
        data: data.map(mapReceiptApiRow),
        meta: {
          page: meta.pageNumber,
          pageSize: meta.pageSize,
          total: meta.total,
          totalPages: meta.totalPages,
        },
      };
    },
    staleTime: 1000 * 60 * 5,
  });
}
